/**
 * What this session may do with money on the RUN side (DNKADM-0375 / DNKAPI-0670) — the
 * ONE seam the DNKAPI-0670 money controls read, so the console hides what the server will
 * refuse and asks for the manager PIN where the server will ask for it.
 *
 * Each permission is `true` (allowed), `'pin'` (allowed with the manager PIN in the
 * request's `managerPin`), or `false` (hidden). DNKAPI-0670 rules:
 *  - `canAdjustCredits` — granting credits through Adjust. STAFF is refused
 *    (`403 STAFF_NOT_ALLOWED`); a free grant goes through Comp, behind its PIN.
 *  - `canCancelFullOrNone` — a Full or None refund choice on a CONFIRMED booking. Policy,
 *    and any choice on an unconfirmed request or a hold, never need it.
 *  - `canOverrideNoShowFee` — a custom amount on a no-show OR a check-in. Charging the
 *    default amount is always allowed.
 *  - `canWaiveNoShowFee` (DNKAPI-0678, DNKADM-0469) — waiving (`charge:false`) a no-show fee
 *    on a booking with a card to charge. A booking with no card has nothing to waive.
 *  - `canRemoveCards` / `canBlockGuests` (DNKAPI-0675, DNKADM-0465) — Remove and Replace a
 *    member's saved card; Block / Unblock a member's guest. Both ADMIN only on the server,
 *    so `false` for STAFF.
 *
 * **The read (Phase B).** A STAFF session reads `GET /v2/admin/me/permissions`
 * ({@link getMyMoneyPermissions}), with the booking's `locationId` when the caller has one:
 * a location with no manager PIN set then answers `false` for the PIN permissions.
 * An OWNER/ADMIN/SUPER_ADMIN (any non-STAFF) session is answered without a request — the
 * server's contract is "all true" for those roles, so asking would only add a round trip
 * to every dialog. While the read loads, or if it fails, {@link interimMoneyPermissions}
 * stands in (STAFF: no Adjust, PIN for the three PIN permissions, no card Remove, no guest
 * Block) — the server checks every call anyway.
 *
 * ADMIN-class money gates outside DNKAPI-0670 (pending-fee Adjust/Waive, order Refund/Void,
 * Manage credits) stay on the session tier, not on this seam.
 *
 * Presentation only. The server checks the role and the PIN on every call.
 */
import { useCallback } from 'react';
import { useResource } from '@/hooks/useResource';
import {
  getMyMoneyPermissions,
  type MoneyPermission,
  type MoneyPermissions,
} from '@/api/mePermissions';
import { useStaffTier } from './authHooks';

export type { MoneyPermission, MoneyPermissions };

const ADMIN_MONEY: MoneyPermissions = {
  canAdjustCredits: true,
  canCancelFullOrNone: true,
  canOverrideNoShowFee: true,
  canWaiveNoShowFee: true,
  canComp: true,
  canRemoveCards: true,
  canBlockGuests: true,
};

const STAFF_MONEY: MoneyPermissions = {
  canAdjustCredits: false,
  canCancelFullOrNone: 'pin',
  canOverrideNoShowFee: 'pin',
  canWaiveNoShowFee: 'pin',
  canComp: 'pin',
  canRemoveCards: false,
  canBlockGuests: false,
};

/** The tier-only answer: an admin's, or STAFF's fallback while the read loads or fails. */
export function interimMoneyPermissions(isStaffTier: boolean): MoneyPermissions {
  return isStaffTier ? STAFF_MONEY : ADMIN_MONEY;
}

/**
 * This session's money permissions — at `locationId` when given (the booking's location),
 * else tenant-wide.
 */
export function useMoneyPermissions(locationId?: string | null): MoneyPermissions {
  const isStaffTier = useStaffTier();
  const location = locationId ?? null;
  const read = useResource(
    useCallback(
      () => (isStaffTier ? getMyMoneyPermissions(location) : Promise.resolve(null)),
      [isStaffTier, location],
    ),
    [isStaffTier, location],
  );
  if (!isStaffTier) return ADMIN_MONEY;
  return read.status === 'ready' && read.data !== null ? read.data : STAFF_MONEY;
}
